/**
 * Military/Veteran Auto Loan Calculator Utilities
 * 
 * This file contains calculation functions for Australian Defence Force (ADF) 
 * members and veterans' car loan benefits and rates.
 */

// Define the input interface for the calculator
export interface MilitaryLoanInputs {
  vehiclePrice: number;         // Purchase price of the vehicle
  downPayment: number;          // Initial deposit
  loanTerm: number;             // Loan term in months
  interestRate: number;         // Annual interest rate (%)
  serviceStatus: string;        // 'active', 'veteran', 'reserve', or 'civilian'
  serviceLength: number;        // Years of service (for discount eligibility)
  isInjuryPension: boolean;     // Whether receiving DVA disability pension
  balloonPayment: number;       // Optional balloon payment at end
  applicationFee: number;       // Loan application fee
  annualFee: number;            // Annual loan maintenance fee
}

// Define the output interface
export interface MilitaryLoanResult {
  monthlyPayment: number;
  totalInterest: number;
  totalFees: number;
  totalCost: number;
  loanAmount: number;
  amortizationSchedule: AmortizationRow[];
  discountedRate: number;
  rateDiscount: number;
  feeDiscount: number;
  originalRate: number;
  eligibleBenefits: string[];
  comparisonRate: number;
}

// Define the amortization row interface
export interface AmortizationRow {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
  fees: number;
}

// Constants for ADF and veteran benefits
const ADF_BENEFITS = {
  // Rate discounts based on service status
  RATE_DISCOUNTS: {
    active: 0.50,    // 0.50% off for active service members
    veteran: 0.35,   // 0.35% off for veterans
    reserve: 0.25,   // 0.25% off for reserves
    civilian: 0.0    // No discount for civilians
  },
  
  // Fee discounts
  APPLICATION_FEE_DISCOUNT: 0.5,  // 50% off application fee for eligible members
  
  // Service length thresholds for additional benefits
  SERVICE_LENGTH_THRESHOLD: 5,     // Years of service needed for additional discount
  SERVICE_LENGTH_DISCOUNT: 0.15,   // Additional 0.15% off for 5+ years of service
  
  // DVA injury pension additional discount
  INJURY_PENSION_DISCOUNT: 0.25,   // Additional 0.25% off for DVA disability pension recipients
  
  // Minimum service length for fee waiver
  FEE_WAIVER_SERVICE_LENGTH: 10    // Years of service needed for fee waiver
};

/**
 * Calculate military/veteran auto loan details
 * 
 * @param inputs Object containing all required inputs for calculation
 * @returns MilitaryLoanResult with calculation results and benefits
 */
export function calculateMilitaryLoan(inputs: MilitaryLoanInputs): MilitaryLoanResult {
  const {
    vehiclePrice,
    downPayment,
    loanTerm,
    interestRate,
    serviceStatus,
    serviceLength,
    isInjuryPension,
    balloonPayment,
    applicationFee,
    annualFee
  } = inputs;

  // Calculate base loan amount
  const loanAmount = vehiclePrice - downPayment;
  
  // Store original rate for comparison
  const originalRate = interestRate;
  
  // Calculate rate discount based on service status and other factors
  let rateDiscount = ADF_BENEFITS.RATE_DISCOUNTS[serviceStatus as keyof typeof ADF_BENEFITS.RATE_DISCOUNTS] || 0;
  
  // Add service length discount if eligible
  if (serviceLength >= ADF_BENEFITS.SERVICE_LENGTH_THRESHOLD) {
    rateDiscount += ADF_BENEFITS.SERVICE_LENGTH_DISCOUNT;
  }
  
  // Add injury pension discount if applicable
  if (isInjuryPension) {
    rateDiscount += ADF_BENEFITS.INJURY_PENSION_DISCOUNT;
  }
  
  // Apply the discounted rate (ensure it doesn't go below 0)
  const discountedRate = Math.max(0, interestRate - rateDiscount);
  
  // Calculate fee discount
  let appFeeWithDiscount = applicationFee;
  let annualFeeWithDiscount = annualFee;
  let feeDiscount = 0;
  
  // Apply fee discounts based on service status
  if (serviceStatus !== 'civilian') {
    // Apply application fee discount
    feeDiscount += applicationFee * ADF_BENEFITS.APPLICATION_FEE_DISCOUNT;
    appFeeWithDiscount = applicationFee - (applicationFee * ADF_BENEFITS.APPLICATION_FEE_DISCOUNT);
    
    // Waive all fees for long-serving members
    if (serviceLength >= ADF_BENEFITS.FEE_WAIVER_SERVICE_LENGTH) {
      feeDiscount += applicationFee * (1 - ADF_BENEFITS.APPLICATION_FEE_DISCOUNT); // Remaining app fee
      feeDiscount += annualFee * loanTerm / 12; // All annual fees
      appFeeWithDiscount = 0;
      annualFeeWithDiscount = 0;
    }
  }
  
  // Convert annual rate to monthly
  const monthlyRate = discountedRate / 100 / 12;
  
  // Calculate monthly payment
  const monthlyPayment = calculateMonthlyPayment(loanAmount, monthlyRate, loanTerm, balloonPayment);
  
  // Generate amortization schedule
  const amortizationSchedule = generateAmortizationSchedule(
    loanAmount, 
    monthlyRate, 
    loanTerm, 
    monthlyPayment, 
    balloonPayment, 
    annualFeeWithDiscount
  );
  
  // Calculate total interest
  const totalInterest = amortizationSchedule.reduce((sum, month) => sum + month.interest, 0);
  
  // Calculate total fees
  const totalFees = appFeeWithDiscount + (annualFeeWithDiscount * Math.ceil(loanTerm / 12));
  
  // Calculate comparison rate
  const comparisonRate = calculateComparisonRate(
    loanAmount, 
    discountedRate, 
    loanTerm / 12, 
    appFeeWithDiscount, 
    annualFeeWithDiscount
  );
  
  // Determine eligible benefits
  const eligibleBenefits = determineEligibleBenefits(
    serviceStatus, 
    serviceLength, 
    isInjuryPension
  );
  
  // Return the result
  return {
    monthlyPayment,
    totalInterest,
    totalFees,
    totalCost: loanAmount + totalInterest + totalFees,
    loanAmount,
    amortizationSchedule,
    discountedRate,
    rateDiscount,
    feeDiscount,
    originalRate,
    eligibleBenefits,
    comparisonRate
  };
}

/**
 * Calculate monthly payment
 */
function calculateMonthlyPayment(
  principal: number, 
  monthlyRate: number, 
  term: number, 
  balloonPayment: number
): number {
  if (monthlyRate === 0) {
    // If rate is 0%, it's a simple division plus balloon adjustment
    return (principal - balloonPayment) / term;
  }
  
  // Standard formula with balloon adjustment
  return (
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, term))) / 
    (Math.pow(1 + monthlyRate, term) - 1)
  ) - (
    (balloonPayment * monthlyRate) / 
    (Math.pow(1 + monthlyRate, term) - 1)
  );
}

/**
 * Generate amortization schedule
 */
function generateAmortizationSchedule(
  principal: number,
  monthlyRate: number,
  totalPeriods: number,
  monthlyPayment: number,
  balloonPayment: number,
  annualFee: number
): AmortizationRow[] {
  let balance = principal;
  const schedule: AmortizationRow[] = [];
  
  for (let month = 1; month <= totalPeriods; month++) {
    const interestPayment = balance * monthlyRate;
    let principalPayment = monthlyPayment - interestPayment;
    
    // For the last payment, add the balloon amount
    if (month === totalPeriods) {
      principalPayment += balloonPayment;
    }
    
    balance -= principalPayment;
    
    // Add annual fee on 12-month intervals (starting from month 12)
    const fees = (month % 12 === 0) ? annualFee : 0;
    
    schedule.push({
      month,
      payment: month === totalPeriods ? monthlyPayment + balloonPayment : monthlyPayment,
      principal: principalPayment,
      interest: interestPayment,
      balance: Math.max(0, balance),
      fees
    });
  }
  
  return schedule;
}

/**
 * Calculate comparison rate based on Australian rules
 */
export function calculateComparisonRate(
  loanAmount: number,
  interestRate: number,
  loanTerm: number,
  applicationFee: number,
  annualFee: number
): number {
  // Simplified comparison rate calculation
  const totalFees = applicationFee + (annualFee * loanTerm);
  const feePercentage = (totalFees / loanAmount) * 100;
  
  // Annualized fee percentage
  const annualizedFeePercentage = feePercentage / loanTerm;
  
  // Add the annualized fee percentage to the interest rate
  return interestRate + annualizedFeePercentage;
}

/**
 * Determine eligible benefits based on service profile
 */
function determineEligibleBenefits(
  serviceStatus: string,
  serviceLength: number,
  isInjuryPension: boolean
): string[] {
  const benefits: string[] = [];
  
  if (serviceStatus === 'active') {
    benefits.push('ADF Active Service Rate Discount');
    benefits.push('50% Application Fee Discount');
  } else if (serviceStatus === 'veteran') {
    benefits.push('Veteran Rate Discount');
    benefits.push('50% Application Fee Discount');
  } else if (serviceStatus === 'reserve') {
    benefits.push('ADF Reserve Rate Discount');
    benefits.push('50% Application Fee Discount');
  }
  
  if (serviceLength >= ADF_BENEFITS.SERVICE_LENGTH_THRESHOLD) {
    benefits.push('Long Service Additional Rate Discount');
  }
  
  if (serviceLength >= ADF_BENEFITS.FEE_WAIVER_SERVICE_LENGTH) {
    benefits.push('Full Fee Waiver (10+ Years Service)');
  }
  
  if (isInjuryPension) {
    benefits.push('DVA Disability Pension Additional Rate Discount');
  }
  
  return benefits;
}

/**
 * Format currency for display
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/**
 * Format percentage for display
 */
export function formatPercentage(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
} 